// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { userIdentifierSchema } from '@/account/api/get-organization-users';
import { EditablePropertyButton } from '@/components/editable-property-button';
import { FormPersonSearch } from '@/components/form-person-search';
import { PersonLabel } from '@/components/person-label';
import { PopoverForm } from '@/components/popover-form';
import { SupportSnackbar } from '@/components/support-snackbar';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useTimeout } from '@/hooks/use-timeout';
import { Ticket } from '@/ticket/api/types';
import { useUpdateTicket } from '@/ticket/hooks/use-update-ticket';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Stack, useTheme } from '@mui/material';
import { useRef } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

const formSchema = z.object({ assignTo: userIdentifierSchema.nullable() });

type FormData = z.infer<typeof formSchema>;

interface Props {
  ticket: Ticket;
}

export function TicketAssignToControl(props: Props) {
  const theme = useTheme();
  const editor = useDisclosure();
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const { control, reset, handleSubmit } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      assignTo: props.ticket.assignedTo ? props.ticket.assignedTo : null,
    },
  });

  const updateTicket = useUpdateTicket(props.ticket.key, {
    onSuccess: (ticket) => {
      reset({ assignTo: ticket.assignedTo ?? null });
      editor.close();
    },
  });

  const timeout = useTimeout(reset, theme.transitions.duration.short);

  const handleClose = () => {
    editor.close();
    timeout.start();
  };

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    if (data.assignTo?.id === props.ticket.assignedTo?.id) {
      return editor.close();
    }

    updateTicket.mutate({
      assignedTo: data.assignTo?.id ?? null,
    });
  };

  return (
    <Box>
      <EditablePropertyButton ref={buttonRef} onClick={editor.open}>
        <Stack direction="row" spacing={1}>
          <PersonLabel name={props.ticket.assignedTo} />
        </Stack>
      </EditablePropertyButton>
      <SupportSnackbar
        isOpen={updateTicket.isError}
        onClose={updateTicket.reset}
        message="An error occurred while updating the ticket."
      />
      <PopoverForm
        position="left"
        anchorElement={buttonRef.current}
        isOpen={editor.isOpen}
        onCancel={handleClose}
        onSubmit={handleSubmit(handleValidSubmission)}
        width="300px"
      >
        <FormPersonSearch control={control} name={'assignTo'} />
      </PopoverForm>
    </Box>
  );
}
